import { useState, useEffect } from "react";
import { playerService } from "../../services/api";

// Accept 'identifiers' prop instead of 'player'
const PlayerHighlights = ({ identifiers, refreshKey = 0 }) => {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch player highlights from API
  useEffect(() => {
    const fetchHighlights = async () => {
      if (!identifiers?.wyscoutId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await playerService.getPublicPlayerHighlights(
          identifiers.wyscoutId
        );
        if (response.success) {
          setHighlights(response.data);
        } else {
          setError(response.error || "Failed to load highlights");
        }
      } catch (err) {
        console.error("Error fetching highlights:", err);
        setError("Failed to load highlights");
      } finally {
        setLoading(false);
      }
    };

    fetchHighlights();
  }, [identifiers?.wyscoutId, refreshKey]);

  // Generate thumbnail for video (placeholder for now)
  const getThumbnail = (link) => {
    if (!link)
      return "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=225&fit=crop&crop=center";

    // For YouTube links, extract video ID and generate thumbnail URL
    if (link.includes("youtube.com") || link.includes("youtu.be")) {
      try {
        let videoId = "";

        if (link.includes("youtube.com/watch?v=")) {
          const url = new URL(link);
          videoId = url.searchParams.get("v");
        } else if (link.includes("youtu.be/")) {
          videoId = link.split("youtu.be/")[1].split("?")[0];
        }

        if (videoId) {
          return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        }
      } catch (err) {
        console.error("Error parsing YouTube URL:", err);
      }
    }

    return "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=225&fit=crop&crop=center";
  };

  return (
    <div className="bg-white rounded-[20px] overflow-hidden">
      {/* Content */}
      <div className="p-6 bg-[#F8F9FB] overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
          </div>
        ) : highlights.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No highlights available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {highlights.map((highlight) => (
              <div
                key={highlight.id}
                className="bg-white rounded-lg overflow-hidden border border-[#D6DBEA] hover:shadow-md transition-shadow duration-200"
              >
                {/* Video Thumbnail */}
                <div className="relative group cursor-pointer">
                  <a
                    href={highlight.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={getThumbnail(highlight.url)}
                      alt={highlight.title}
                      className="w-full h-48 object-cover"
                    />
                    {/* Play icon overlay - always visible like in the image */}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-black ml-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </a>
                </div>

                {/* Title */}
                <div className="p-4">
                  <h3 className="font-medium text-base text-[#222A35] leading-tight">
                    {highlight.title || "Untitled Highlight"}
                  </h3>
                  {highlight.created_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(highlight.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerHighlights;
